import { useCallback, useEffect, useState } from "react";

type Carousel = {
  readonly index: number;
  /** Jumps to a slide and restarts the rotation timer. */
  readonly goTo: (index: number) => void;
};

/**
 * Auto-advancing index for a list of `length` slides.
 * Stays idle for a single slide, so a one-prize carousel costs nothing.
 */
export function useCarousel(length: number, intervalMs: number): Carousel {
  const [index, setIndex] = useState(0);
  const [tick, setTick] = useState(0);

  const goTo = useCallback(
    (next: number) => {
      if (length === 0) return;

      setIndex(((next % length) + length) % length);
      // Bumping the tick restarts the effect below, so a manual pick
      // gets a full interval before the carousel moves on.
      setTick((current) => current + 1);
    },
    [length],
  );

  useEffect(() => {
    if (length < 2 || intervalMs <= 0) return;

    const timer = window.setInterval(() => {
      setIndex((current) => (current + 1) % length);
    }, intervalMs);

    return () => {
      window.clearInterval(timer);
    };
  }, [length, intervalMs, tick]);

  return { index: length === 0 ? 0 : Math.min(index, length - 1), goTo };
}
